// Inline address pill used on the landing page in place of the various
// "Get Your Free Assessment" CTAs. Mirrors LookupAddressForm's autosuggest
// behaviour exactly (same redux actions, same suggestion source) but drops
// the title and county-support note. Covered Texas suggestions route to
// /verify; external-geocoder suggestions route to /national-intake. Both
// destination pages see `selectedAddress` already in redux.
import React, { useCallback, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Form,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Spinner,
} from "reactstrap";
import Autosuggest from "react-autosuggest";
import PropTypes from "prop-types";

import {
  AddressPillWrap,
  SmallSpinner,
} from "../FreeAssessmentPage/styled";
import "../FreeAssessmentPage/autosuggest-theme.css";
import {
  AddressNotFoundMessage,
  AddressSearchErrorMessage,
  AddressSuggestionsPortal,
  PropertyIcon,
  renderAddressSuggestion,
  useAddressAutosuggest,
} from "../../components/AddressAutosuggest";
import { nationalIntakeEntryUrl } from "../../lib/nationalIntakeRoute";
import {
  getAddressSuggestions as getAddressSuggestionsAction,
  getExternalAddressSuggestions as getExternalAddressSuggestionsAction,
  clearAddressSuggestions as clearAddressSuggestionsAction,
  setAddress,
} from "../../redux/actions/assessment";

export const HomepageAddressEntry = ({
  addressSuggestions,
  suggestionsFailed,
  isLoading,
  getAddressSuggestions,
  getExternalSuggestions,
  clearSuggestions,
  selectAddress,
  placeholder,
  autosuggestId,
}) => {
  const history = useHistory();
  // Tracks per-instance focus so we only swap home→spinner on the pill
  // the user is typing in. addressSuggestions / isLoading live in shared
  // redux state, so without this gate every instance would flicker
  // simultaneously.
  const [hasFocus, setHasFocus] = useState(false);
  const {
    address,
    getDropdownStyle,
    handleChange,
    onSuggestionFetchRequested,
    onSuggestionsClearRequested,
    searchEveryState,
    selectSuggestion,
    showAddressNotFound,
    showEscapeHatch,
    wrapperRef,
  } = useAddressAutosuggest({
    addressSuggestions,
    clearSuggestions,
    getAddressSuggestions,
    getExternalSuggestions,
    isLoading,
    isActive: hasFocus,
    selectAddress,
    suggestionsFailed,
  });

  const getSuggestionValue = (suggestion) => {
    const formatted = selectSuggestion(suggestion);
    // Covered DB addresses stay in the Texas report flow. Addresses that only
    // came back from the external geocoder (``covered: false``) enter the
    // national flow, which performs its own serviceability check and captures
    // demand when the property cannot yet be served.
    history.push(
      suggestion.covered === false
        ? nationalIntakeEntryUrl(suggestion)
        : "/verify"
    );
    return formatted;
  };

  const renderSuggestionsContainer = ({ containerProps, children }) => (
    <AddressSuggestionsPortal
      containerProps={containerProps}
      getDropdownStyle={getDropdownStyle}
      onSearchEveryState={searchEveryState}
      showEscapeHatch={showEscapeHatch}
    >
      {children}
    </AddressSuggestionsPortal>
  );

  // Prefetch the /verify chunk the moment the user signals intent
  // (focus or first keystroke).  By the time they pick a suggestion,
  // ``getSuggestionValue`` calls history.push("/verify") and
  // App.js's React.lazy resolves from the prefetch instead of a cold
  // network round-trip.  This was the dominant contributor to the
  // INP-on-mobile metric Search Console flagged in May 2026 — the
  // route change after a suggestion-pick averaged 200ms+ because
  // the FreeAssessmentPage chunk hadn't even started downloading
  // until the click fired.  Webpack returns the same module promise
  // on repeat calls, so triggering the import multiple times is
  // free once the chunk is cached.
  const prefetchVerify = useCallback(() => {
    import(
      /* webpackPrefetch: true */
      "../FreeAssessmentPage"
    ).catch(() => {
      // Network blip — the real navigation will retry from the
      // user's tap, no point surfacing this prefetch failure.
    });
  }, []);

  const inputProps = {
    onChange: handleChange,
    onFocus: () => {
      setHasFocus(true);
      prefetchVerify();
    },
    onBlur: () => setHasFocus(false),
    value: address,
    placeholder,
    type: "text",
    autoComplete: "street-address",
    "aria-label": "Enter your property address",
  };

  const showSpinner = isLoading && hasFocus;

  // The wrapping div lifts this instance into its own stacking context
  // above neighbouring sections — without it, sibling sections (with their
  // own position/transform-induced stacking contexts) paint on top of the
  // dropdown. Dropping `alwaysRenderSuggestions` means only the focused
  // pill renders suggestions, so typing in one instance doesn't open the
  // dropdown on the other instances that read the same redux slice.
  return (
    <div ref={wrapperRef} style={{ position: "relative", zIndex: 1000, width: "100%" }}>
      <Form
        className="register-form"
        onSubmit={(e) => e.preventDefault()}
        style={{ width: "100%" }}
      >
        <AddressPillWrap>
          <InputGroup className="form-group-no-border">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                {showSpinner ? (
                  <SmallSpinner as={Spinner} color="info" />
                ) : (
                  <PropertyIcon />
                )}
              </InputGroupText>
            </InputGroupAddon>
            <Autosuggest
              id={autosuggestId}
              suggestions={addressSuggestions}
              renderSuggestion={renderAddressSuggestion}
              renderSuggestionsContainer={renderSuggestionsContainer}
              onSuggestionsFetchRequested={onSuggestionFetchRequested}
              onSuggestionsClearRequested={onSuggestionsClearRequested}
              getSuggestionValue={getSuggestionValue}
              inputProps={inputProps}
              renderInputComponent={(p) => <Input {...p} />}
            />
          </InputGroup>
        </AddressPillWrap>
        {showAddressNotFound && <AddressNotFoundMessage />}
        {suggestionsFailed && address.trim() && !isLoading ? (
          <AddressSearchErrorMessage
            onRetry={() => getAddressSuggestions(address)}
          />
        ) : null}
      </Form>
    </div>
  );
};

HomepageAddressEntry.propTypes = {
  addressSuggestions: PropTypes.array,
  suggestionsFailed: PropTypes.bool,
  isLoading: PropTypes.bool,
  getAddressSuggestions: PropTypes.func.isRequired,
  getExternalSuggestions: PropTypes.func.isRequired,
  clearSuggestions: PropTypes.func.isRequired,
  selectAddress: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  autosuggestId: PropTypes.string.isRequired,
};

HomepageAddressEntry.defaultProps = {
  addressSuggestions: [],
  suggestionsFailed: false,
  isLoading: false,
  placeholder: "Enter your property address",
};

const mapStateToProps = (state) => ({
  addressSuggestions: state.freeAssessment.addressSuggestions,
  suggestionsFailed: state.freeAssessment.suggestionsFailed,
  isLoading: state.freeAssessment.loading.addressSuggestions,
});

const mapDispatchToProps = (dispatch) => ({
  getAddressSuggestions: (str) => dispatch(getAddressSuggestionsAction(str)),
  getExternalSuggestions: (str) => dispatch(getExternalAddressSuggestionsAction(str)),
  clearSuggestions: () => dispatch(clearAddressSuggestionsAction()),
  selectAddress: (address) => dispatch(setAddress(address)),
});

export default connect(mapStateToProps, mapDispatchToProps)(HomepageAddressEntry);
